import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { keyframes, styled } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CanceledError } from "axios";
import { OrderDirection } from "common/contracts/order-direction";
import PaginationParameters from "common/contracts/pagination-parameters";
import Guid from "common/values/guid/guid";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useSession } from "users/session/session-context";
import CommentThread from "work/entities/comment-thread/comment-thread";
import FeeScheduleTemplateAPIService from "work/entities/fee-schedule-template/api/fee-schedule-template-api-service";
import FeeScheduleTemplateQuery, { FeeScheduleTemplateFilterParameters, FeeScheduleTemplateOrderField, FeeScheduleTemplateOrderParameters } from "work/entities/fee-schedule-template/api/request-contracts/query-fee-schedule-template-api-request";
import FeeScheduleTemplate from "work/entities/fee-schedule-template/fee-schedule-template";
import {
  FreelyPatchableFields,
  ProposalField,
} from "work/entities/proposal/proposal";
import { FeeScheduleRedline } from "work/entities/proposal/redlining/fee-schedule-redline/fee-schedule-redline";
import ProposalRedline from "work/entities/proposal/redlining/proposal-redline";
import FeeScheduleRedlineSelection from "work/entities/proposal/redlining/view/redline-field-inputs/fee-schedule-redline-selection";
import { Masked } from "work/entities/work-agreement/work-agreement";
import FeeScheduleCategory, {
  FeeScheduleCategoryMaskedCurrency
} from "work/values/fee-schedule-category/fee-schedule-category";
import ProposalIssues, { ProposalIssueLevel } from "work/values/proposal-issues/proposal-issues";
import {
  FeeScheduleTagChip,
  TemplateList,
  TemplateLoader
} from "../../../../fee-schedule-template/view/embedded-containers";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    border-color: rgb(21, 101, 192);
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ListContainer = styled(Grid)(
  () => ({
    minHeight: "20rem",
  }));
const TemplateContainer = styled(Grid)(
  ({ theme }) => ({
    height: "25rem",
    overflow: "auto",
    [theme.breakpoints.down("lg")]: {
      width: "100%",
    },
  }));
const FeeScheduleGrid = styled(Grid)(
  () => ({
    height: "25rem",
    overflow: "auto",
  }));
const RedlineCategorySelection = styled(FeeScheduleRedlineSelection)(
  ({ theme }) => ({
    [theme.breakpoints.down("lg")]: {
      marginTop: theme.spacing(2),
    },
    height: "100%",
    marginLeft: theme.spacing(2),
  })
);
const IssueContainer = styled("div")<{ active?: boolean }>(
  ({ theme, active }) => ({
    animation: active ? `${pulseBorder} 1.5s ease-in-out infinite` : "none",
    margin: active ? theme.spacing(1) : 'initial',
    marginLeft: 0,
    marginBottom: theme.spacing(1),
    padding: active ? theme.spacing(1) : "initial",
    borderRadius: theme.shape.borderRadius,
  }));

type FeeScheduleTabProps = {
  isOpen: boolean;
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  activeReviewField: ProposalField | undefined;
  commentThreads: CommentThread[];
  disableEditing?: boolean;
  onProposalRedlineChange: (
    newRedline: ProposalRedline,
    traversalFieldOverride?: ProposalField | null
  ) => void;
  onTraverseToNewField?: (fieldInfo: ProposalField | undefined) => void;
  onCommentsClicked: (field: ProposalField, name?: string) => void;

  fieldsToPatch?: FreelyPatchableFields;

  onFeeScheduleTemplatesUpdated: (templateIds: Guid[], newProposalRedline?: ProposalRedline) => void;
};

const FeeScheduleTab = (props: Readonly<FeeScheduleTabProps>) => {
  const {
    isOpen,
    proposalRedline,
    activeReviewField,
    commentThreads,
    disableEditing,
    onProposalRedlineChange,
    onTraverseToNewField,
    onCommentsClicked,

    fieldsToPatch,

    onFeeScheduleTemplatesUpdated,
  } = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [templateFeeSchedules, setTemplateFeeSchedules] = useState<
    FeeScheduleTemplate[]
  >([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Guid[]>([]);
  const [paginatedParameters, setPaginatedParameters] = useState<PaginationParameters>();

  const issues = React.useMemo(() => props.issues?.entries.filter(issue => [
    ProposalField.FeeSchedule,
    ProposalField.FeeScheduleCategory(issue.field.id ?? Guid.empty),
  ].includes(issue.field)), [props.issues?.entries]);

  const isLargeDownDisplay = useMediaQuery((theme) =>
    theme.breakpoints.down("lg")
  );
  const session = useSession();

  useEffect(
    () => {
      let abortController = new AbortController();
      void getFeeScheduleTemplates(abortController);
      setSelectedTemplateIds(fieldsToPatch?.feeScheduleTemplateIds ?? []);

      return () => {
        abortController.abort();
        abortController = new AbortController();
      };
    },
    []
  );

  async function getFeeScheduleTemplates(abortController: AbortController) {
    const entityId = session?.currentEntity.id;
    if (!entityId) return;

    setIsLoading(true);
    try {
      const filterParams = new FeeScheduleTemplateFilterParameters(entityId);

      const orderParameters = new FeeScheduleTemplateOrderParameters(
        FeeScheduleTemplateOrderField.Name,
        OrderDirection.Descending
      );

      const templateQuery: FeeScheduleTemplateQuery = new FeeScheduleTemplateQuery(
        filterParams,
        orderParameters,
        paginatedParameters ?? new PaginationParameters(0, 100)
      );

      const feeScheduleService = new FeeScheduleTemplateAPIService(session);
      const templates =
        await feeScheduleService.getFeeScheduleTemplates(
          templateQuery,
          abortController
        );
      setTemplateFeeSchedules(templates.data);
    }
    catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
      enqueueSnackbar(
        "Failed to load fee schedule templates.",
        {
          variant: "error",
        }
      );
    } finally {
      setPaginatedParameters((prev) => {
        if (prev) {
          return new PaginationParameters(prev.pageIndex, prev.pageSize);
        }
      });
      setIsLoading(false);
    }
  }

  function handleTemplateExpanded(template: FeeScheduleTemplate) {
    let templates = [...templateFeeSchedules];
    let selectedTemplate = templates.find((feeSchedule) =>
      feeSchedule.id?.isEqualTo(template.id)
    );
    template.isExpanded = !selectedTemplate?.isExpanded;

    setTemplateFeeSchedules(templates);
    onCommentsClicked(
      ProposalField.FeeSchedule,
      undefined
    );
  }

  function handleTemplateSelectionToggled(template: FeeScheduleTemplate) {
    if (Masked.isMasked(proposalRedline.feeSchedule)) {
      throw new Error("Cannot modify fee schedule when it is masked.");
    }

    try {
      if (!template.id) {
        // noinspection ExceptionCaughtLocallyJS
        throw new Error("No template selected");
      }

      let updatedSelectedTemplateIds = [...selectedTemplateIds];
      const isAlreadySelected = selectedTemplateIds.some((id) =>
        id.isEqualTo(template.id)
      );

      let newProposalRedline: ProposalRedline | undefined = undefined;

      if (isAlreadySelected) {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds.filter(
          (id) => !id.isEqualTo(template.id)
        );
      } else {
        updatedSelectedTemplateIds.push(template.id);

        let newFeeScheduleRedline: FeeScheduleRedline =
          proposalRedline.feeSchedule;
        for (const category of template.categories) {
          newFeeScheduleRedline = newFeeScheduleRedline.addEntry(category);
        }
        newProposalRedline = proposalRedline.updateFeeScheduleRedline(
          newFeeScheduleRedline
        );
      }

      setSelectedTemplateIds(updatedSelectedTemplateIds);
      onFeeScheduleTemplatesUpdated(
        updatedSelectedTemplateIds,
        newProposalRedline
      );
      onCommentsClicked(
        ProposalField.FeeSchedule,
        undefined
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to apply template.",
        {
          variant: "error",
        }
      );
    } finally {
      setIsLoading(false);
    }
  }

  function handleTemplateCategoryToggled(
    event: React.MouseEvent,
    templateCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null
  ) {
    event.stopPropagation();
    if (Masked.isMasked(proposalRedline.feeSchedule))
      throw new Error("Cannot modify fee schedule when it is masked.");

    const feeScheduleCategory = templateCategory?.clone();

    if (!feeScheduleCategory) return;

    if (getIsCategoryAlreadySelected(feeScheduleCategory)) {
      const updatedRedline = proposalRedline.updateFeeScheduleRedline(
        proposalRedline.feeSchedule.removeEntryByFieldId(feeScheduleCategory.id)
      );

      onProposalRedlineChange(
        updatedRedline,
        null
      );
    } else {
      const updatedRedline = proposalRedline.updateFeeScheduleRedline(
        proposalRedline.feeSchedule.addEntry(feeScheduleCategory)
      );
      onProposalRedlineChange(
        updatedRedline,
        null
      );
    }
    onCommentsClicked(
      ProposalField.FeeSchedule,
      undefined
    );
  }

  function getIsCategoryAlreadySelected(
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null
  ): boolean {
    if (Masked.isMasked(proposalRedline.feeSchedule)) {
      throw new Error("Cannot check category when fee schedule is masked.");
    }

    if (!category) return false;
    return proposalRedline.feeSchedule.redlines.some(
      (categoryRedline) =>
        categoryRedline.field.id?.isEqualTo(category.id) &&
        categoryRedline.currentEntry !== null
    );
  }

  function renderTemplates() {
    return (
      <TemplateList subheader={<ListSubheader>Templates</ListSubheader>}>
        {templateFeeSchedules.length === 0 && (
          <>
            {isLoading ? (
              <TemplateLoader size={32} thickness={4} disableShrink />
            ) : (
              <ListItem key="noTemplates">
                <ListItemText>No Templates</ListItemText>
              </ListItem>
            )}
          </>
        )}
        {templateFeeSchedules.map((template: FeeScheduleTemplate) => {
          const isSelected = selectedTemplateIds.some((id) =>
            id.isEqualTo(template.id)
          );
          const feeScheduleCategories = template.categories.map(
            (templateCategory) => templateCategory.clone()
          );
          const indeterminate =
            !isSelected &&
            feeScheduleCategories.some(
              (category) => category && getIsCategoryAlreadySelected(category)
            );

          return (
            <div key={template.id?.value}>
              <ListItem
                secondaryAction={
                  <Tooltip
                    title={`${template.isExpanded ? "Hide" : "Show"
                      } Categories`}
                  >
                    <span>
                      <IconButton
                        edge="end"
                        onClick={() => handleTemplateExpanded(template)}
                        size="medium"
                      >
                        {template.isExpanded ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                }
              >
                <ListItemIcon>
                  <Tooltip
                    title={
                      !isSelected ? "Select Template" : "Deselect Template"
                    }
                  >
                    <span>
                      <Checkbox
                        edge="start"
                        color="primary"
                        checked={isSelected}
                        indeterminate={indeterminate}
                        tabIndex={-1}
                        disableRipple
                        onChange={() =>
                          handleTemplateSelectionToggled(template)
                        }
                      />
                    </span>
                  </Tooltip>
                </ListItemIcon>
                <ListItemText
                  primary={template.name}
                  secondary={template.tags.map((tag) => (
                    <FeeScheduleTagChip
                      key={tag.value}
                      size="small"
                      label={tag.value}
                    />
                  ))}
                />
              </ListItem>
              <Collapse
                in={template.isExpanded}
                timeout="auto"
                style={{ backgroundColor: "#FAFAFA" }}
              >
                <Divider />
                <List disablePadding dense>
                  {renderTemplateCategories(template)}
                </List>
                <Divider />
              </Collapse>
            </div>
          );
        })}
      </TemplateList>
    );
  }

  function renderTemplateCategories(template: FeeScheduleTemplate) {
    return template.categories.map((category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => (
      <ListItem
        key={category.name.value}
        onClick={(event) => handleTemplateCategoryToggled(
          event,
          category
        )}
        secondaryAction={
          <Checkbox
            edge="end"
            color="primary"
            checked={getIsCategoryAlreadySelected(category)}
            tabIndex={-1}
            disableRipple
            onClick={(event) => handleTemplateCategoryToggled(
              event,
              category
            )}
          />
        }
      >
        <ListItemText
          disableTypography
          primary={category.name.value}
          secondary={
            <>
              <div>{category.description?.value}</div>
              <div>{category instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" : category.feeString}</div>
            </>
          }
        />
      </ListItem>
    ));
  }

  if (!isOpen) return null;
  if (Masked.isMasked(proposalRedline.feeSchedule)) {
    return "You do not have permission to view the fee schedule.";
  }

  return (
    <ListContainer container direction="row">
      {!disableEditing && (
        <TemplateContainer>
          {renderTemplates()}
          {isLargeDownDisplay && <Divider />}
        </TemplateContainer>
      )}
      <FeeScheduleGrid size="grow">
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer>
            {issues?.map((issue) => (
              <Typography
                key={issue.field?.key}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
        <RedlineCategorySelection
          activeReviewField={activeReviewField}
          commentThreads={commentThreads}
          disableEditing={disableEditing}
          feeScheduleRedline={proposalRedline.feeSchedule}
          onCommentsClicked={onCommentsClicked}
          onFeeScheduleRedlineChange={(
            newFeeScheduleRedline: FeeScheduleRedline,
            traversalFieldOverride?: ProposalField | null
          ) => {
            try {
              onProposalRedlineChange(
                proposalRedline.updateFeeScheduleRedline(newFeeScheduleRedline),
                traversalFieldOverride
              );
            } catch (error) {
              console.error(error);
              enqueueSnackbar(
                "Failed to update fee schedule. Please try again",
                {
                  variant: "error",
                }
              );
            }
          }}
          onCategoryClicked={(categoryId: Guid) => {
            onTraverseToNewField?.(
              ProposalField.FeeScheduleCategory(categoryId)
            );
          }}
        />
      </FeeScheduleGrid>
    </ListContainer>
  );
};

export default FeeScheduleTab;
